import {createReduxStore, register, useDispatch, useSelect} from '@wordpress/data';
import {apiFetch as API_FETCH, controls as dataControls} from '@wordpress/data-controls';
import {STORE_KEY as LANDING_STORE_KEY} from './landing';

export const STORE_KEY = 'epi/categories';
const STORAGE_KEY = 'epi_categories_cache_v1';

const now = () => new Date().getTime();

function loadPersisted() {
    try {
        if (typeof window === 'undefined') return {};
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
        return {};
    }
}

function savePersisted(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
    }
}

const persisted = loadPersisted();

const DEFAULT_STATE = {
    tree: Array.isArray(persisted.tree) ? persisted.tree : [],
    loading: false,
    error: null,
    fetchedAt: persisted.fetchedAt || 0,
    hash: typeof persisted.hash === 'string' ? persisted.hash : null,
};

const actions = {
    * fetchCategoryTree({force = false} = {}) {
        const landingHash = yield {
            type: 'FETCH_LANDING_HASH_FOR_SYNC'
        };

        const state = yield {type: 'GET_STATE'};

        const effectiveForce = !!force
            || (landingHash && state.hash !== landingHash)
            || (!state.tree || (Array.isArray(state.tree) && state.tree.length === 0));

        if (!effectiveForce) {
            return;
        }

        try {
            yield {type: 'LOADING'};
            const tree = yield API_FETCH({
                path: '/api/categories/tree',
                headers: {'X-WP-Nonce': (typeof epiData !== 'undefined' ? epiData.nonce : undefined)}
            });

            yield {type: 'RECEIVE', tree, hash: landingHash};
        } catch (err) {
            yield {type: 'FAILED', error: err};
        }
    },

    * syncWithLanding() {
        const landingHash = yield {
            type: 'FETCH_LANDING_HASH_FOR_SYNC'
        };
        const state = yield {type: 'GET_STATE'};

        if (landingHash && state.hash !== landingHash) {
            yield actions.fetchCategoryTree({force: true});
        }
    }
};

function reducer(state = DEFAULT_STATE, action) {
    switch (action.type) {
        case 'LOADING':
            return {...state, loading: true, error: null};
        case 'RECEIVE': {
            const next = {
                ...state,
                tree: action.tree || [],
                loading: false,
                error: null,
                fetchedAt: now(),
                hash: action.hash || state.hash
            };
            savePersisted({
                tree: next.tree,
                fetchedAt: next.fetchedAt,
                hash: next.hash
            });
            return next;
        }
        case 'FAILED':
            return {...state, loading: false, error: action.error};
        default:
            return state;
    }
}

const controls = {
    ...dataControls,
    FETCH_LANDING_HASH_FOR_SYNC: () => {
        return wp.data.dispatch(LANDING_STORE_KEY).fetchLandingHash();
    },
    GET_STATE: () => {
        return wp.data.select(STORE_KEY).getState();
    }
};

export const store = createReduxStore(STORE_KEY, {
    reducer,
    actions,
    controls,
    selectors: {
        getState(s) {
            return s;
        },
        getTree(s) {
            return s.tree;
        },
        getLoading(s) {
            return s.loading;
        },
        getError(s) {
            return s.error;
        }
    }
});

register(store);

export const useCategories = () => {
    const tree = useSelect(select => select(STORE_KEY).getTree(), []);
    const loading = useSelect(select => select(STORE_KEY).getLoading(), []);
    const error = useSelect(select => select(STORE_KEY).getError(), []);
    const dispatch = useDispatch(STORE_KEY);

    return {
        tree,
        loading,
        error,
        fetchCategoryTree: dispatch.fetchCategoryTree,
    };
};
